import { CardPrimitive } from '@figma/fpl-components';
import { color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardActionArea } from '@/UI/Card/Card';
import { Grid } from '@/UI/Grid/Grid';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { toMillis } from '@/utils/folder.utils';
import { I18N_KEYS } from '@/language/keys';
import { useUserStore } from '@/state/user.state';
import { useGoToWorkflow } from '@/hooks/useGoToWorkflow';
import { DashboardWorkflow } from '@/types/workflows';
import { FlexCol } from '@/UI/styles';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { dashboardDisplayName, dashboardRowId } from './utils/dashboard.utils';
import { DashboardCard } from './DashboardCard';
import { DashboardInlineEditInput } from './DashboardInlineEditInput';
import WorkflowCardDisplay from './components/WorkflowCardDisplay';
import type { DashboardItem, EditingNameState } from './DashboardMain';

const styles = stylex.create({
  root: {
    background: 'none',
    isolation: 'isolate',
    maxWidth: 345,
    position: 'relative',
  },
  grab: {
    cursor: 'grab',
  },
  grabbing: {
    cursor: 'grabbing',
  },
  editing: {
    cursor: 'default',
  },
  mainButton: {
    background: 'none',
    borderRadius: 'inherit',
    outline: {
      default: 'none',
      ':focus-visible': `2px solid ${fplColor.borderSelected}`,
    },
    outlineOffset: '2px',
  },
});

interface WorkflowCardProps {
  workflow: DashboardWorkflow & { owner?: string; ownerName?: string };
  workflowEditState: EditingNameState;
  handleContextMenu: (
    e: React.MouseEvent<HTMLDivElement>,
    id: string,
    itemType?: Extract<DashboardItem, 'workflow'>,
    /** Row identity, so a both-ways share's two cards don't resolve to each other. */
    rowId?: string,
  ) => void;
  handleRenameWorkflow: (id: string, name: string) => void;
  handleCancelRenameWorkflow: () => void;
  isDragging?: boolean;
  onDragStart?: (e: React.DragEvent, itemId: string) => void;
  onDragEnd?: () => void;
  showOwner?: boolean;
}

export const WorkflowCard = ({
  workflow,
  workflowEditState,
  handleContextMenu,
  handleRenameWorkflow,
  handleCancelRenameWorkflow,
  isDragging = false,
  onDragStart,
  onDragEnd,
  showOwner = false,
}: WorkflowCardProps) => {
  const [editingValue, setEditingValue] = useState(workflow.name);
  // The two cards of a both-ways share hold the same `id`, so keys and test ids key off the row.
  const rowId = dashboardRowId(workflow);

  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [isHovered, setIsHovered] = useState(false);
  const currentUser = useUserStore((state) => state.user);
  const goToWorkflow = useGoToWorkflow();
  const isEditing = workflowEditState.kind === 'editing' && workflowEditState.id === workflow.id;

  useEffect(() => {
    if (isEditing) {
      setEditingValue(workflow.name);
    }
  }, [isEditing, workflow.name]);

  const handleClick = () => {
    if (!isEditing) {
      goToWorkflow(workflow);
    }
  };

  const handleConfirmRename = useCallback(() => {
    handleRenameWorkflow(workflow.id, editingValue);
  }, [editingValue, handleRenameWorkflow, workflow.id]);

  const handleCancelRename = useCallback(() => {
    setEditingValue(workflow.name);
    handleCancelRenameWorkflow();
  }, [handleCancelRenameWorkflow, workflow.name]);

  const subtitle = showOwner
    ? currentUser && workflow.owner && workflow.owner === currentUser.uid
      ? t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.OWNED_BY_YOU)
      : t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.OWNED_BY_OTHER, {
          owner: workflow.ownerName || '-',
        })
    : workflow.updatedAt
      ? t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_LAST_EDITED, {
          date: getTimeDiffInWords(toMillis(workflow.updatedAt)),
        })
      : t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_CREATED, {
          date: getTimeDiffInWords(toMillis(workflow.createdAt)),
        });

  if (isFplMigrationEnabled === true) {
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
        <CardPrimitive.Root
          data-testid={`recipe-card-${rowId}`}
          {...stylex.props(styles.root, isEditing ? styles.editing : isDragging ? styles.grabbing : styles.grab)}
          onContextMenu={(e) => {
            e.stopPropagation();
            handleContextMenu(e, workflow.id, 'workflow', rowId);
          }}
          draggable={!isEditing}
          onDragStart={(e) => onDragStart?.(e, workflow.id)}
          onDragEnd={onDragEnd}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <CardPrimitive.MainButton
            {...stylex.props(styles.mainButton)}
            aria-label={t(I18N_KEYS.GENERAL.ARIA.OPEN_NAMED_ITEM, { name: dashboardDisplayName(workflow) })}
            onClick={handleClick}
          />
          <WorkflowCardDisplay poster={workflow.poster} isHovered={isHovered} />
          <CardPrimitive.Interactive>
            <FlexCol sx={{ pl: 1, pt: 1, pb: 3, pr: 2 }}>
              <DashboardInlineEditInput
                isEditing={isEditing}
                value={editingValue}
                displayValue={dashboardDisplayName(workflow)}
                onChange={setEditingValue}
                onConfirm={handleConfirmRename}
                onCancel={handleCancelRename}
                data-testid={`recipe-title-${rowId}`}
              />
              <TextSmRg sx={{ color: fplColor.textSecondary }}>{subtitle}</TextSmRg>
            </FlexCol>
          </CardPrimitive.Interactive>
        </CardPrimitive.Root>
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
      <DashboardCard
        data-testid={`recipe-card-${rowId}`}
        onContextMenu={(e) => {
          e.stopPropagation();
          handleContextMenu(e, workflow.id, 'workflow', rowId);
        }}
        draggable={!isEditing}
        onDragStart={(e) => onDragStart?.(e, workflow.id)}
        onDragEnd={onDragEnd}
        sx={{
          cursor: isEditing ? 'default' : isDragging ? 'grabbing' : 'grab',
        }}
      >
        <CardActionArea
          disableRipple
          sx={{ position: 'relative', '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 } }}
          onClick={handleClick}
        >
          <WorkflowCardDisplay poster={workflow.poster} />
        </CardActionArea>
        <FlexCol sx={{ pl: 1, pt: 1, pb: 3, pr: 2 }}>
          <DashboardInlineEditInput
            isEditing={isEditing}
            value={editingValue}
            displayValue={dashboardDisplayName(workflow)}
            onChange={setEditingValue}
            onConfirm={handleConfirmRename}
            onCancel={handleCancelRename}
            data-testid={`recipe-title-${rowId}`}
          />
          <TextSmRg sx={{ color: fplColor.textSecondary }}>
            {showOwner
              ? currentUser && workflow.owner && workflow.owner === currentUser.uid
                ? t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.OWNED_BY_YOU)
                : t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.OWNED_BY_OTHER, {
                    owner: workflow.ownerName || '-',
                  })
              : workflow.updatedAt
                ? t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_LAST_EDITED, {
                    date: getTimeDiffInWords(toMillis(workflow.updatedAt)),
                  })
                : t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_CREATED, {
                    date: getTimeDiffInWords(toMillis(workflow.createdAt)),
                  })}
          </TextSmRg>
        </FlexCol>
      </DashboardCard>
    </Grid>
  );
};
